/**
 * useWorldCupLayout
 * Encapsulates welcome-screen state, prefetch logic, tab-press listener,
 * and tab navigation for the WorldCup stack layout.
 */

import { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { worldCupLeaderboardQueryOptions } from './useWorldCupLeaderboardQuery';
import { worldCupGroupsQueryOptions } from './useWorldCupGroupsQuery';
import {
  WORLD_CUP_COMPETITION_ID,
} from '../worldcup.constants';
import {
  WORLD_CUP_PREFETCH_DELAY_MS,
  WORLD_CUP_INTER_PREFETCH_DELAY_MS,
} from '../constants/worldcupReactQuery.constants';

/**
 * Top-N size for the leaderboard prefetch fired when the user lands on the
 * picks tab. Mirrors the page's initial visible slice so the second view is
 * served from cache.
 */
const WORLD_CUP_LEADERBOARD_PREFETCH_LIMIT = 50;
import { WORLD_CUP_BASE_PATH } from '../../competitions/contexts/competitionContext.constants';
import { competitionTabRoute } from '../../competitions/helpers/competitionRoutes.helper';
import type { WorldCupTab } from '../types/worldcup.types';
import {
  readWorldCupWelcomeCompleted,
  writeWorldCupWelcomeCompleted,
} from '../helpers/worldcupWelcomeStorage.helper';
import { enforceImmersiveMode } from '../../shared/helpers/immersiveMode.helper';
import { useWorldCupWelcomeMenuStore } from '../stores/worldCupWelcomeMenuStore';
import { useHasExistingChallenges } from '../../challenges/hooks/useHasExistingChallenges';

type WelcomeUiState = 'loading' | 'show' | 'hidden';

interface UseWorldCupLayoutOptions {
  isLoaded: boolean;
  isSignedIn: boolean | null;
  userId: string | null;
  activeTab: WorldCupTab;
  apiClient: Parameters<typeof worldCupLeaderboardQueryOptions>[0];
}

interface UseWorldCupLayoutResult {
  welcomeUi: WelcomeUiState;
  handleWelcomeFinished: () => Promise<void>;
  handleTabChange: (tab: WorldCupTab) => void;
}

export function useWorldCupLayout({
  isLoaded,
  isSignedIn,
  userId,
  activeTab,
  apiClient,
}: UseWorldCupLayoutOptions): UseWorldCupLayoutResult {
  const router = useRouter();
  const queryClient = useQueryClient();
  const setWelcomeOverlayVisible = useWorldCupWelcomeMenuStore((s) => s.setWelcomeOverlayVisible);

  const [welcomeUi, setWelcomeUi] = useState<WelcomeUiState>('loading');
  const { isResolved: challengesResolved, hasChallenges } = useHasExistingChallenges();

  useEffect(() => {
    // Wait for identity before deciding which key to read. A signed-in user whose
    // id has not resolved yet would otherwise read the 'anonymous' key, flash the
    // welcome screen, then re-read once userId lands — re-showing the intro to a
    // returning user on the same account. Stay in 'loading' (renders nothing) until
    // we know who they are; this guard only defers, it never blocks a real session.
    if (!isLoaded) return;
    if (isSignedIn && !userId) return;
    // For signed-in users, also wait for the challenge probe so an established
    // player (≥1 challenge) skips the WC intro instead of briefly flashing it.
    // The probe resolves as "has challenges" on error, so a flaky network
    // suppresses the intro rather than re-showing it.
    if (isSignedIn && !challengesResolved) return;
    let cancelled = false;
    void (async () => {
      const done = await readWorldCupWelcomeCompleted(userId);
      if (cancelled) return;
      if (!done && isSignedIn && hasChallenges) {
        // Established account: backfill completion so the intro never appears.
        void writeWorldCupWelcomeCompleted(userId);
        setWelcomeUi('hidden');
        return;
      }
      setWelcomeUi(done ? 'hidden' : 'show');
    })();
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, userId, challengesResolved, hasChallenges]);

  useEffect(() => {
    setWelcomeOverlayVisible(welcomeUi === 'show');
    return () => {
      setWelcomeOverlayVisible(false);
    };
  }, [setWelcomeOverlayVisible, welcomeUi]);

  const handleWelcomeFinished = useCallback(async () => {
    try {
      await writeWorldCupWelcomeCompleted(userId);
    } finally {
      await enforceImmersiveMode();
      setWelcomeUi('hidden');
    }
  }, [userId]);

  // Prefetch leaderboard + groups data when user lands on picks tab
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (activeTab !== 'picks') return;

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      if (isCancelled) return;
      await queryClient.prefetchQuery(
        worldCupLeaderboardQueryOptions(
          apiClient,
          WORLD_CUP_COMPETITION_ID,
          WORLD_CUP_LEADERBOARD_PREFETCH_LIMIT,
        ),
      );
      if (isCancelled) return;
      await new Promise((resolve) => setTimeout(resolve, WORLD_CUP_INTER_PREFETCH_DELAY_MS));
      if (isCancelled) return;
      await queryClient.prefetchQuery(
        worldCupGroupsQueryOptions(apiClient, WORLD_CUP_COMPETITION_ID),
      );
    }, WORLD_CUP_PREFETCH_DELAY_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [activeTab, apiClient, isLoaded, isSignedIn, queryClient]);

  const handleTabChange = useCallback(
    (tab: WorldCupTab) => {
      if (tab === activeTab) return;
      router.replace(competitionTabRoute(WORLD_CUP_BASE_PATH, tab) as never);
    },
    [activeTab, router],
  );

  return { welcomeUi, handleWelcomeFinished, handleTabChange };
}
